import React, { useContext, useState, useEffect, useRef } from 'react';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Stack from '@mui/material/Stack';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import Alert from '@mui/material/Alert';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import { BiBarcode } from 'react-icons/bi';
import AppContext from '../../context/AppContext';
import BarcodeSettings from './BarcodeSettings';
import QRSettings from './QRSettings';
import QRCode from 'react-qr-code';
import Barcode from 'react-barcode';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { toPng, toJpeg, toSvg } from 'html-to-image';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import { format } from 'date-fns';

const QRBarcodeArea = () => {
  const { data, setData } = useContext(AppContext);

  const [currentItem, setCurrentItem] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (currentItem == null && data.items.length > 0) {
      setCurrentItem(data.items[0]);
    }
    // eslint-disable-next-line
  }, [data]);

  const ref = useRef(null);

  const handleDownload = async () => {
    if (ref.current === null) {
      return;
    }
    setLoading(true);
    var zip = new JSZip();

    for (let item of data.items) {
      setCurrentItem(item);
      let file;
      let dataUrl;
      switch (data.download.type) {
        case 'JPG':
          dataUrl = await toJpeg(ref.current, {
            cacheBust: true,
            style: { border: 'none' },
          });
          break;
        case 'PNG':
          dataUrl = await toPng(ref.current, {
            cacheBust: true,
            style: { border: 'none' },
          });
          break;
        case 'SVG':
          dataUrl = await toSvg(ref.current, {
            cacheBust: true,
            style: { border: 'none' },
          });
          break;
        default:
          dataUrl = await toJpeg(ref.current, {
            cacheBust: true,
            style: { border: 'none' },
          });
      }
      file = (await fetch(dataUrl)).blob();
      // console.log(item.value);
      // console.log(data.download.type);
      // console.log(item.value, data.download.type));
      zip.file(`${item.value}.${data.download.type}`, file, {
        createFolders: false,
      });
    }
    zip.file(
      '_hello.txt',
      'Thank you for using QRBarcodeTools!(https://qrbarcodetools.com) \n . Need some Custom Design with the Codes ? Drop me a mail at david@davidamunga.com',
    );
    zip.generateAsync({ type: 'blob' }).then(function (content) {
      setLoading(false);
      saveAs(
        content,
        `QRBarcodeTools-${format(new Date(), 'dd-MM-yy-HHmmaa')}.zip`,
      );
    });
    // eslint-disable-next-line
  };

  return (
    <Stack direction="column" spacing={2}>
      <Card>
        <CardContent>
          <Typography sx={{ fontSize: 14 }} color="text.secondary" gutterBottom>
            Configuration
          </Typography>
          <Stack direction="column">
            <Stack
              direction="column"
              justifyContent="center"
              alignItems="center"
              spacing={2}
            >
              <ToggleButtonGroup
                onChange={(e, newValue) => {
                  if (newValue) {
                    setData({ ...data, type: newValue });
                  }
                }}
                value={data.type}
                fullWidth
                color="primary"
                exclusive
              >
                <ToggleButton value="1D">
                  1D
                  <BiBarcode size={30} />
                </ToggleButton>
                <ToggleButton value="2D">
                  2D
                  <QrCode2Icon fontSize="large" />
                </ToggleButton>
              </ToggleButtonGroup>

              {/*  Options */}
              {data.type.includes('1D') && <BarcodeSettings />}
              {data.type.includes('2D') && <QRSettings />}
            </Stack>
          </Stack>
        </CardContent>
      </Card>
      <Card>
        <CardContent>
          <Typography sx={{ fontSize: 14 }} color="text.secondary" gutterBottom>
            Result
          </Typography>
          <Stack spacing={2} alignItems="center" direction="column">
            <div
              ref={ref}
              style={{
                display: 'flex',
                justifyContent: 'center',
                width: '100%',
                background: '#FFFFFF',
              }}
            >
              {data.type.includes('1D') && (
                <Barcode
                  value={
                    currentItem === null ? 'QRBarcode Tools' : currentItem.value
                  }
                  textAlign={data.barcodeSettings.textAlign}
                  font={data.barcodeSettings.font}
                  lineColor={data.barcodeSettings.lineColor}
                  background={data.barcodeSettings.background}
                  margin={data.barcodeSettings.margin}
                  displayValue={data.barcodeSettings.showText}
                  width={data.barcodeSettings.barWidth}
                  height={data.barcodeSettings.barHeight}
                  fontSize={data.barcodeSettings.fontSize}
                />
              )}

              {data.type.includes('2D') && (
                <QRCode
                  bgColor={data.qrSettings.backgroundColor}
                  fgColor={data.qrSettings.foregroundColor}
                  level={data.qrSettings.errorCorrectionLevel}
                  size={data.qrSettings.size}
                  value={
                    currentItem === null ? 'QRBarcode Tools' : currentItem.value
                  }
                />
              )}
            </div>

            {loading && (
              <Stack direction="row">
                <Alert
                  sx={{ px: 10 }}
                  fullWidth
                  icon={<HourglassBottomIcon fontSize="inherit" />}
                  severity="success"
                >
                  Preparing Data....
                  <strong>{`${data.items.indexOf(currentItem) + 1}/${
                    data.items.length
                  }`}</strong>
                </Alert>
              </Stack>
            )}
            <Stack direction="row" justifyContent="center" spacing={4}>
              <FormControl>
                <InputLabel id="export-option">Export As</InputLabel>

                <Select
                  label="Export As"
                  labelId="export-option"
                  id="export-item-option"
                  value={data.download.type}
                  onChange={(e) =>
                    setData({
                      ...data,
                      download: { ...data.download, type: e.target.value },
                    })
                  }
                >
                  <MenuItem value="png">PNG</MenuItem>
                  <MenuItem value="jpg">JPG</MenuItem>
                  <MenuItem value="svg">SVG</MenuItem>
                </Select>
              </FormControl>

              <Button
                color="secondary"
                disabled={loading}
                variant="contained"
                onClick={() => handleDownload()}
                startIcon={<CloudDownloadIcon />}
              >
                Download
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    </Stack>
  );
};

export default QRBarcodeArea;
